// Utilities for handling subtitle/transcript formats (SRT, WebVTT, YouTube
// paste format). Shared between the server and the client so that both can
// detect and parse subtitle files consistently.

import * as unicodePatterns from "./unicodePatterns.js";

// A single cue timestamp: `HH:MM:SS,mmm` (SRT), `HH:MM:SS.mmm` (WebVTT), or
// the WebVTT no-hour form `MM:SS.mmm`. WebVTT allows more than 2 hour digits
// for very long media.
const cueTimestampSource = "(?:(\\d{1,4}):)?(\\d{1,2}):(\\d{2})[,.](\\d{1,3})";

/**
 * Detects whether a transcript is in SRT (SubRip) or WebVTT format. Cues have
 * timestamp ranges like `00:00:01,000 --> 00:00:04,000` (SRT),
 * `00:00:01.000 --> 00:00:04.000` (WebVTT), or the no-hour WebVTT form
 * `00:01.000 --> 00:04.000` — none of which the YouTube paste format contains.
 *
 * @param {string} transcript
 * @returns {boolean}
 */
export const isSRTTranscript = (transcript) => {
  if (!transcript || typeof transcript !== "string") {
    return false;
  }
  return new RegExp(
    `${cueTimestampSource}\\s*-->\\s*${cueTimestampSource}`
  ).test(transcript);
};

const srtCueTimeRegexp = new RegExp(
  `^\\s*${cueTimestampSource}\\s*-->\\s*${cueTimestampSource}`
);

const wordOrPunctuationRegexp = /[‘’\p{L}\p{M}]+|[0-9]+|[«»”””.,?!¡¿]/gu;

const namedCueEntities = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: "\u00A0",
  lrm: "\u200E",
  rlm: "\u200F",
};

/**
 * Decodes the character references WebVTT (and, in practice, SRT) cue text
 * may contain — named (`&amp;`), decimal (`&#233;`), and hex (`&#xE9;`) — so
 * entities neither leak into the extracted plain text nor produce phantom
 * timing tokens like `amp`. Unknown references are left untouched. Must run
 * after tag stripping so `&lt;i&gt;` survives as literal text.
 *
 * @param {string} text
 * @returns {string}
 */
const decodeCueEntities = (text) =>
  text.replace(
    /&(?:#x([0-9a-f]+)|#([0-9]+)|([a-z]+));/gi,
    (match, hex, decimal, name) => {
      if (hex || decimal) {
        const codePoint = hex ? parseInt(hex, 16) : parseInt(decimal, 10);
        return codePoint <= 0x10ffff ? String.fromCodePoint(codePoint) : match;
      }
      return namedCueEntities[name.toLowerCase()] ?? match;
    }
  );

/**
 * Cleans the raw text lines of one cue: joins them, strips inline formatting
 * markup (`<i>`, WebVTT internal timestamp tags, `{...}` style overrides),
 * decodes character references, and collapses whitespace. Shared by
 * parseSRTTranscript and extractPlainTextSRT so timing tokens and extracted
 * plain text always tokenize identically — e.g. `<i>Hel</i>lo` is the single
 * word `Hello` to both, otherwise generateAudioMap would find no sync point.
 *
 * @param {string[]} cueTextLines
 * @returns {string}
 */
const cleanCueText = (cueTextLines) =>
  decodeCueEntities(
    cueTextLines
      .join(" ")
      .replace(/<[^>]*>/g, "")
      .replace(/\{[^}]*\}/g, "")
  )
    .replace(/[ \t]+/g, " ")
    .trim();

const cjkThaiRegExp = new RegExp(unicodePatterns.cjkThai, "u");
const combiningMarkRegExp = new RegExp(`[${unicodePatterns.mark}]`, "u");

/**
 * Splits one matched word run into the tokens the reader displays as
 * clickable spans. Most words stay whole, but runs containing
 * Han/Hiragana/Katakana/Thai characters are split into one token per
 * character (keeping combining marks attached to the preceding character),
 * mirroring spannify.js. Without this, timings parsed from a transcript in
 * those scripts could never align with the reader's per-character spans.
 *
 * @param {string} word
 * @returns {string[]}
 */
export const splitWordIntoReaderTokens = (word) => {
  if (!cjkThaiRegExp.test(word)) {
    return [word];
  }

  const tokens = [];
  for (let index = 0; index < word.length; index++) {
    let token = word[index];
    while (
      index < word.length - 1 &&
      combiningMarkRegExp.test(word[index + 1])
    ) {
      index++;
      token += word[index];
    }
    tokens.push(token);
  }
  return tokens;
};

/**
 * Expands timed words from an external transcription (e.g. OpenAI Whisper)
 * into reader tokens. Transcription "words" in per-character scripts can span
 * several characters (often with surrounding punctuation or spaces), which
 * would never match the reader's per-character spans; such words are
 * re-tokenized with the timing kept on the first token. Words without
 * per-character scripts pass through untouched.
 *
 * @param {{word: string; start?: number; end?: number}[]} wordTimings
 * @returns {{word: string; start?: number; end?: number}[]}
 */
export const splitTimedWordsIntoReaderTokens = (wordTimings) => {
  /** @type {{word: string; start?: number; end?: number}[]} */
  const result = [];

  for (const wordTiming of wordTimings) {
    if (!cjkThaiRegExp.test(wordTiming.word)) {
      result.push(wordTiming);
      continue;
    }

    let firstToken = true;
    for (const match of wordTiming.word.match(wordOrPunctuationRegexp) ?? []) {
      for (const word of splitWordIntoReaderTokens(match)) {
        result.push({
          word,
          ...(firstToken && wordTiming.start !== undefined
            ? { start: wordTiming.start }
            : {}),
        });
        firstToken = false;
      }
    }
  }

  return result;
};

/**
 * Parses an SRT (SubRip) transcript and returns a list of words with start
 * times attached to the first word of each cue.
 *
 * Example input:
 *
 *   1
 *   00:00:00,000 --> 00:00:04,000
 *   I love
 *
 *   2
 *   00:00:05,000 --> 00:00:09,000
 *   chocolate cookies
 *
 * Produces:
 *   [
 *     { word: 'I', start: 0 },
 *     { word: 'love' },
 *     { word: 'chocolate', start: 5 },
 *     { word: 'cookies' },
 *   ]
 *
 * Also accepts WebVTT-style timestamps with periods (e.g.
 * `00:00:00.000 --> 00:00:04.000`), including the no-hour form
 * (`00:01.000 --> 00:04.000`), and SRT formatting tags inside cue text are
 * ignored.
 *
 * @param {string} transcript - The SRT transcript.
 * @param {number} [offset=0] - Seconds to add to each word's start time.
 * @returns {{word: string; start?: number}[]}
 */
export const parseSRTTranscript = (transcript, offset) => {
  offset = offset ?? 0;

  const wordTimings = [];
  const lines = transcript.split(/\r?\n/);
  let index = 0;

  while (index < lines.length) {
    const timeMatch = srtCueTimeRegexp.exec(lines[index]);
    if (!timeMatch) {
      index++;
      continue;
    }

    const hours = timeMatch[1] === undefined ? 0 : parseInt(timeMatch[1], 10);
    const minutes = parseInt(timeMatch[2], 10);
    const seconds = parseInt(timeMatch[3], 10);
    const millis = parseInt(timeMatch[4].padEnd(3, "0"), 10);
    const cueStart = Math.max(
      hours * 3600 + minutes * 60 + seconds + millis / 1000 + offset,
      0
    );

    index++;

    const cueTextLines = [];
    while (index < lines.length && lines[index].trim() !== "") {
      cueTextLines.push(lines[index]);
      index++;
    }

    const cueText = cleanCueText(cueTextLines);

    let firstWordOfCue = true;
    for (const match of cueText.match(wordOrPunctuationRegexp) ?? []) {
      for (const word of splitWordIntoReaderTokens(match)) {
        wordTimings.push({
          word,
          ...(firstWordOfCue ? { start: cueStart } : {}),
        });
        firstWordOfCue = false;
      }
    }
  }

  return wordTimings;
};

/**
 * Extracts plain text from an SRT transcript by stripping the cue index
 * lines, timestamp ranges, and inline formatting tags.
 *
 * @param {string} transcript
 * @returns {string}
 */
export const extractPlainTextSRT = (transcript) => {
  if (!transcript || typeof transcript !== "string") {
    return "";
  }

  const textLines = [];
  const lines = transcript.split(/\r?\n/);
  let index = 0;

  while (index < lines.length) {
    const timeMatch = srtCueTimeRegexp.exec(lines[index]);
    if (!timeMatch) {
      index++;
      continue;
    }

    index++;

    const cueTextLines = [];
    while (index < lines.length && lines[index].trim() !== "") {
      cueTextLines.push(lines[index]);
      index++;
    }

    const cueText = cleanCueText(cueTextLines);
    if (cueText) {
      textLines.push(cueText);
    }
  }

  return textLines.join("\n");
};

/**
 * Extracts an 11-character YouTube video ID from user input which may be a
 * bare ID or any of the common YouTube URL formats (watch?v=, youtu.be/,
 * /live/).
 *
 * @param {string} userInput
 * @returns {string | undefined} the video ID, or undefined if none found
 */
export const extractYouTubeVideoID = (userInput) => {
  if (!userInput || typeof userInput !== "string") {
    return undefined;
  }

  const trimmedInput = userInput.trim();
  const idOnlyRegExp = /^[a-zA-Z0-9_-]{11}$/;
  if (idOnlyRegExp.test(trimmedInput)) {
    return trimmedInput;
  }

  // cspell: disable-next-line
  const idInURLRegExp =
    /(v=|youtu\.be\/|\/live\/|\/shorts\/)([a-zA-Z0-9_-]{11})/;
  const match = idInURLRegExp.exec(trimmedInput);
  if (match && match[2]) {
    return match[2];
  }

  return undefined;
};
